import { h, Component } from "preact";
import { Index } from "algoliasearch";
import { AppConfig, FAQAlgoliaHit, FormattedResult } from "../ts/types";

import debounce from "@common/helpers/debounce";
import Results from "./Results";
import Pagination from "./Pagination";
import noResultImg from "../assets/no-results.png";
import { AutodataCustomEventTypes } from "../../../autodata/src/ts/types";

const URL_SEARCH_TEXT_QUERY_PARAM = "faqSearchText";

interface Props {
  appConfig: AppConfig;
  searchInputDOMNode: HTMLInputElement;
  formatResults(hits: FAQAlgoliaHit[]): FormattedResult[];
  searchClient: Index;
  algoliaSearchFilters: string;
}

interface State {
  loading: boolean;
  searchText: string;
  results: FormattedResult[];
  currentPage: number;
  resultsPerPage: number;
  totalResults: number;
  totalPages: number;
}

const INITIAL_SEARCH_STATE: Omit<State, "loading"> = {
  searchText: "",
  results: [],
  currentPage: 0,
  resultsPerPage: 0,
  totalResults: 0,
  totalPages: 0,
};

export default class Main extends Component<Props, State> {
  constructor() {
    super();

    this.state = {
      ...INITIAL_SEARCH_STATE,
      loading: false,
    };

    this.fetchAlgoliaResults = this.fetchAlgoliaResults.bind(this);
    this.fetchAlgoliaResultsSuccess = this.fetchAlgoliaResultsSuccess.bind(
      this
    );
    this.onInputChanged = this.onInputChanged.bind(this);
    this.onPaginationChanged = this.onPaginationChanged.bind(this);
    this.onKeyboardNavigationChanged = this.onKeyboardNavigationChanged.bind(
      this
    );
    this.updateSearchList = this.updateSearchList.bind(this);
  }

  componentDidMount() {
    this.props.searchInputDOMNode.addEventListener(
      "input",
      this.debouncedInputChangeHandler
    );

    document.addEventListener("keydown", this.onKeyboardNavigationChanged);

    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has(URL_SEARCH_TEXT_QUERY_PARAM)) {
      const searchText = searchParams.get(URL_SEARCH_TEXT_QUERY_PARAM)!;

      this.props.searchInputDOMNode.value = searchText;
      this.onInputChanged(searchText);
    }
  }

  componentDidUpdate(_, previousState: Readonly<State>): void {
    if (
      previousState.loading &&
      !this.state.loading &&
      this.state.totalResults > 0
    ) {
      window.dispatchEvent(
        new CustomEvent(AutodataCustomEventTypes.ON_ALGOLIA_REQUEST_SUCCESS, {
          detail: {
            dataEvent: {
              category: "faq|result",
              action: "returnRequest",
              label: this.state.searchText,
              val: this.state.totalResults,
            },
          },
        })
      );
    }
  }

  componentWillUnmount() {
    this.props.searchInputDOMNode.removeEventListener(
      "input",
      this.debouncedInputChangeHandler
    );

    document.removeEventListener("keydown", this.onKeyboardNavigationChanged);
  }

  get debouncedInputChangeHandler() {
    return debounce(
      ({ target: { value } }) => this.onInputChanged(value),
      200,
      false
    );
  }

  onInputChanged(value) {
    if (value.length > 1) {
      this.setState({
        ...INITIAL_SEARCH_STATE,
        searchText: value,
        loading: true,
      });

      this.fetchAlgoliaResults(value).then(this.fetchAlgoliaResultsSuccess);
    } else {
      this.setState({
        ...INITIAL_SEARCH_STATE,
        loading: false,
      });
    }
  }

  onPaginationChanged(searchText, page) {
    this.setState({
      loading: true,
    });

    this.fetchAlgoliaResults(searchText, page).then(
      this.fetchAlgoliaResultsSuccess
    );
  }

  onKeyboardNavigationChanged({ keyCode }) {
    const { totalPages, currentPage, searchText } = this.state;

    // Left arrow
    if (currentPage > 0 && keyCode === 37) {
      this.onPaginationChanged(searchText, currentPage - 1);
    }
    // Right arrow
    else if (currentPage < totalPages - 1 && keyCode === 39) {
      this.onPaginationChanged(searchText, currentPage + 1);
    }
  }

  updateSearchList({ hits, page, nbHits, nbPages }) {
    this.setState({
      loading: false,
      results: this.props.formatResults(hits),
      currentPage: page,
      totalResults: nbHits,
      totalPages: nbPages,
    });
  }

  fetchAlgoliaResults(searchText: string, page = 0) {
    return this.props.searchClient.search({
      query: searchText,
      filters: this.props.algoliaSearchFilters,
      distinct: true,
      hitsPerPage: this.props.appConfig.totalResultsPerPage,
      page,
    });
  }

  fetchAlgoliaResultsSuccess(data) {
    this.updateSearchList(data);
  }

  render(
    { appConfig }: Props,
    {
      loading,
      searchText,
      results,
      currentPage,
      totalResults,
      totalPages,
    }: State
  ) {
    return (
      <div className="app-search-results columns">
        <div className="column col-12 col-md-11 col-mx-auto">
          {loading && <div className="loading loading-lg" />}
          {!loading && totalResults === 0 && (
            <div className="no-result">
              <img
                className="no-result__picture img-responsive"
                src={noResultImg}
                alt="Aucun résultat"
              />
              <p className="no-result__title">
                {appConfig.i18n.no_result_text}
              </p>
            </div>
          )}
          {results.length > 0 && (
            <Results
              results={results}
              currentPage={currentPage}
              searchText={this.state.searchText}
            />
          )}
          {totalResults > 0 && (
            <Pagination
              loading={loading}
              currentPage={currentPage}
              totalPages={totalPages}
              onClickPreviousPage={() =>
                this.onPaginationChanged(searchText, currentPage - 1)
              }
              onClickNextPage={() =>
                this.onPaginationChanged(searchText, currentPage + 1)
              }
            />
          )}
        </div>
      </div>
    );
  }
}
